/**
 * Compact dropdown for switching between Portuguese and English.
 * Persists the choice to localStorage (handled by i18next-browser-languagedetector).
 * Renders a small flag-less label so it fits in sidebars, topbars, and kiosk
 * layouts without competing with primary navigation.
 */
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AVAILABLE_LANGUAGES, type Language } from '@/i18n';

const LANG_LABELS: Record<Language, string> = {
  pt: 'Português',
  en: 'English',
};

export function LanguageToggle({ compact = false }: { compact?: boolean }) {
  const { i18n, t } = useTranslation('common');
  const current = (i18n.resolvedLanguage ?? 'pt') as Language;

  const setLang = (lang: Language) => {
    i18n.changeLanguage(lang);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 h-8 px-2 text-xs"
          aria-label={t('language')}
          title={t('language')}
        >
          <Globe className="h-3.5 w-3.5" />
          {compact ? current.toUpperCase() : LANG_LABELS[current]}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {AVAILABLE_LANGUAGES.map(lang => (
          <DropdownMenuItem
            key={lang}
            onClick={() => setLang(lang)}
            className={lang === current ? 'font-semibold' : ''}
          >
            {LANG_LABELS[lang]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
